import jwtDecode from "jwt-decode";
import type { LogsEvent } from "@datadog/browser-logs";
import { getTenantConfig } from "@/config/tenantConfig";

type DatadogUser = {
  idSeller: number;
  companyId?: number;
};

type DatadogSdk = {
  rum: typeof import("@datadog/browser-rum").datadogRum;
  logs: typeof import("@datadog/browser-logs").datadogLogs;
};

const SERVICE_NAME = "barte-frontend-seller";

let sdk: DatadogSdk | null = null;
let pendingUser: DatadogUser | null = null;

function getDatadogEnvVars() {
  return {
    applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN,
    site: import.meta.env.VITE_DATADOG_SITE || "datadoghq.com",
    env: import.meta.env.VITE_DATADOG_ENV,
    version: import.meta.env.VITE_DATADOG_VERSION || undefined,
  };
}

async function loadSdk(): Promise<DatadogSdk> {
  const [{ datadogRum }, { datadogLogs }] = await Promise.all([
    import("@datadog/browser-rum"),
    import("@datadog/browser-logs"),
  ]);
  return { rum: datadogRum, logs: datadogLogs };
}

export async function initDatadog() {
  const { applicationId, clientToken, site, env, version } = getDatadogEnvVars();
  if (!applicationId || !clientToken) return;
  try {
    sdk = await loadSdk();
    sdk.rum.init({
      applicationId,
      clientToken,
      site,
      service: SERVICE_NAME,
      env,
      version,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
    });
    sdk.logs.init({
      clientToken,
      site,
      service: SERVICE_NAME,
      env,
      version,
      forwardErrorsToLogs: true,
      forwardConsoleLogs: ["warn", "error"],
      sessionSampleRate: 100,
      beforeSend: sanitizeLogEvent,
    });
    setTenantGlobalContext();
    applyPendingUser();
  } catch {
    sdk = null;
  }
}

const BEARER_TOKEN_PATTERN = /Bearer\s+[\w.~+/=-]+/gi;
const SENSITIVE_KEY_PATTERN =
  /authorization|password|token|secret|cookie|cvv|securitycode|cardnumber|cardholder|^pan$/i;
const PAYLOAD_KEY_PATTERN = /^(data|body|params|payload)$/i;
const MAX_REDACTION_DEPTH = 8;
const REDACTED = "[REDACTED]";

function redactSensitiveData(value: unknown, depth = 0): unknown {
  if (typeof value === "string") return value.replace(BEARER_TOKEN_PATTERN, `Bearer ${REDACTED}`);
  if (value === null || typeof value !== "object") return value;
  if (depth >= MAX_REDACTION_DEPTH) return REDACTED;
  if (Array.isArray(value)) return value.map((item) => redactSensitiveData(item, depth + 1));
  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => {
      if (SENSITIVE_KEY_PATTERN.test(key) || PAYLOAD_KEY_PATTERN.test(key)) {
        return [key, REDACTED];
      }
      return [key, redactSensitiveData(entry, depth + 1)];
    })
  );
}

export function sanitizeLogEvent(event: LogsEvent): boolean {
  Object.assign(event, redactSensitiveData(event));
  return true;
}

function setTenantGlobalContext() {
  if (!sdk) return;
  const tenant = getTenantConfig().applicationName;
  sdk.rum.setGlobalContextProperty("tenant", tenant);
  sdk.logs.setGlobalContextProperty("tenant", tenant);
}

function getUserIdFromAuthToken(): string | null {
  const token = localStorage.getItem("barte-auth-token");
  if (!token) return null;
  try {
    return jwtDecode<{ sub?: string }>(token).sub ?? null;
  } catch {
    return null;
  }
}

function applyPendingUser() {
  if (!sdk || !pendingUser) return;
  const datadogUser = {
    id: getUserIdFromAuthToken() || String(pendingUser.idSeller),
    idSeller: pendingUser.idSeller,
    companyId: pendingUser.companyId,
  };
  sdk.rum.setUser(datadogUser);
  sdk.logs.setUser(datadogUser);
}

export function setDatadogUser(user: DatadogUser) {
  if (!user?.idSeller) return;
  pendingUser = user;
  applyPendingUser();
}

export function clearDatadogUser() {
  pendingUser = null;
  if (!sdk) return;
  sdk.rum.clearUser();
  sdk.logs.clearUser();
}
